"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import { usePermissions } from "@/contexts/PermissionContext";
import { useEventDetailStore } from "@/stores/eventDetailStore";
import { StatusPill } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import {
  EVENT_PANEL_ITEMS,
  EKIP_PANEL_ITEMS,
  ADMIN_PANEL_ITEMS,
  filterByPermission,
  isActive,
  type NavEntry,
} from "./nav-model";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";

interface PanelProps {
  kind: "event" | "ekip" | "admin";
  lang: Locale;
  dict: Dictionary;
  pathname: string;
  eventId?: string;
}

const indicator =
  "before:content-[''] before:absolute before:left-0 before:top-[9px] before:bottom-[9px] before:w-[3px] before:rounded-r-[3px] before:bg-brand before:shadow-[0_0_12px_1px_var(--color-brand-glow-45)]";

export function Panel({ kind, lang, dict, pathname, eventId }: PanelProps) {
  const { hasPermission, isSystemAdmin, user } = usePermissions();
  const role = user?.role;
  const s = dict?.layout?.sidebar as Record<string, string> | undefined;

  const event = useEventDetailStore((st) => st.event);
  const guests = useEventDetailStore((st) => st.guests);
  const fetchEvent = useEventDetailStore((st) => st.fetchEvent);

  // Panel is app chrome, not a page — load the event identity itself so the
  // block shows on every event sub-page, not only ones that fetch the event.
  useEffect(() => {
    if (kind === "event" && eventId && event?.id !== eventId) {
      fetchEvent(eventId);
    }
  }, [kind, eventId, event?.id, fetchEvent]);

  let items: NavEntry[];
  let pbackLabel: string;
  let backHref: string | undefined;

  if (kind === "event") {
    // Filter first, then localise the href — an unfiltered panel offers tabs
    // whose pages answer with a permission error the account cannot clear.
    items = filterByPermission(EVENT_PANEL_ITEMS, {
      hasPermission,
      isSystemAdmin,
      role,
    }).map((i) => ({
      ...i,
      href: `/events/${eventId}${i.href}`,
    }));
    pbackLabel = s?.events ?? "Etkinlikler";
    backHref = `/${lang}/events`;
  } else if (kind === "ekip") {
    items = filterByPermission(EKIP_PANEL_ITEMS, {
      hasPermission,
      isSystemAdmin,
      role,
    });
    pbackLabel = "Ekip";
  } else {
    items = ADMIN_PANEL_ITEMS;
    pbackLabel = s?.adminDashboard ?? "Admin";
  }

  const identityEvent =
    kind === "event" && event && event.id === eventId && event.name
      ? event
      : null;
  const capacityReady = !!identityEvent && guests.length > 0;
  const totalPeople = capacityReady
    ? guests.reduce((sum, g) => sum + 1 + g.allowedFriends, 0)
    : 0;
  const inPeople = capacityReady
    ? guests
        .filter((g) => g.checkedIn)
        .reduce((sum, g) => sum + 1 + (g.checkedInFriends || 0), 0)
    : 0;

  const pbackClass =
    "flex items-center gap-1.5 h-[34px] px-2.5 -ml-1 rounded-lg text-[13px] font-medium text-muted hover:bg-brand-glow-08 hover:text-brand-light transition-colors focus-ring";

  return (
    <aside className="flex flex-col h-screen w-[256px] bg-secondary border-r border-border shrink-0 overflow-hidden">
      {/* 60px header band — aligned with the rail's 60px logo line */}
      <div className="h-[60px] flex items-center px-3 border-b border-border shrink-0">
        {backHref ? (
          <Link href={backHref} aria-label={pbackLabel} className={pbackClass}>
            <ChevronLeft className="w-[18px] h-[18px] shrink-0" aria-hidden />
            <span className="truncate">{pbackLabel}</span>
          </Link>
        ) : (
          <span className="text-[13px] font-medium text-muted-foreground px-1.5">
            {pbackLabel}
          </span>
        )}
      </div>

      {/* event identity block */}
      {identityEvent && (
        <div className="px-3 py-3 border-b border-border shrink-0">
          <div className="font-mono text-[9px] font-medium uppercase tracking-[0.12em] text-brand leading-none">
            {s?.event ?? "Etkinlik"}
          </div>
          <div className="font-display text-base font-semibold text-foreground truncate mt-1.5">
            {identityEvent.name}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <StatusPill event={identityEvent} />
            {capacityReady && (
              <>
                <span className="font-mono text-[11.5px] text-[color:var(--color-text-disabled)]">
                  ·
                </span>
                <span className="font-mono text-[11.5px] font-medium tracking-[0.05em] text-muted tabular-nums">
                  {inPeople} / {totalPeople}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <nav className="flex-1 min-h-0 p-3 flex flex-col overflow-y-auto">
        <div className="flex flex-col gap-0.5">
          {items.map((item) => {
            const href = `/${lang}${item.href}`;
            const active = isActive(pathname, href);
            const Icon = item.icon;
            const label = (item.dictKey && s?.[item.dictKey]) || item.fallback;
            return (
              <Link
                key={item.key}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex items-center gap-3 px-3 py-[9px] rounded-[10px]",
                  "text-[13px] font-medium w-full transition-colors focus-ring",
                  active
                    ? cn("bg-brand-glow-12 text-brand-light", indicator)
                    : "text-muted hover:bg-brand-glow-08 hover:text-brand-light",
                )}
              >
                <Icon className="w-4 h-4 shrink-0" aria-hidden />
                <span className="flex-1 truncate">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </aside>
  );
}
